import { useEffect, useState } from 'react'

import { api } from '../../lib/api'
import type { Round } from '../../lib/approvals'
import { roundLabel } from '../../lib/approvals'
import { errorText } from '../../lib/offsite'
import { dayHourMin, hoursToHM } from '../../lib/personFormat'
import { RoundPicker, dayLabel, dowLabel } from './parts'

/* ============================================================================
   เวลาทำงานของทีม (ลูกน้อง) — `/me/team`
   API: GET /api/me/team
        GET /api/me/team/:empCode/{worktime,ot}?round=YYYY/MM
        GET /api/me/team/:empCode/leave?year=YYYY
        backend/src/routes/me.js → lib/subordinates.js (+ worktime/leave/ot)

   หัวหน้าดูข้อมูลของลูกน้องในสายตัวเอง (คนที่ตั้งเราเป็นผู้อนุมัติใน Users)
   — ต่างจากรายงานของ admin ที่เห็นทั้งบริษัท ตรงนี้เห็นเฉพาะทีมตัวเอง

   ── layout: ซ้ายรายชื่อ (ค้นหาได้) ขวารายละเอียด ─────────────────────────────
   ของจริงหัวหน้าคนหนึ่งมีลูกน้องได้ถึง 29 คน ตารางรายชื่อ 5 คอลัมน์ + ปุ่ม
   กินที่ทั้งหน้าจนต้องเลื่อนลงไปดูข้อมูล และกลับมาเปลี่ยนคนต้องเลื่อนขึ้นอีก
   รายชื่อแบบ list แคบ ๆ ที่ค้นได้ ทำให้สลับคนได้โดยไม่เสียตำแหน่งที่อ่านอยู่

   ── ช่วงเวลาของแต่ละแท็บ ────────────────────────────────────────────────────
   เวลาทำงาน/OT = **รอบ** 21 ถึง 20 (ตัดเข้าเงินเดือนตามรอบนี้)
   การลา        = **ปี** โดยตั้งใจ — โควตาวันลาเป็นสิทธิ์ต่อปีปฏิทิน (HR_CAN_LEAVE
                  เก็บเป็นรายปี) ถ้าตัดเป็นรอบ ตัวเลข "ได้/ใช้ไป/คงเหลือ" จะไม่มี
                  ความหมายเพราะสิทธิ์ไม่ได้รีเซ็ตตามรอบ
   ============================================================================ */

type Member = {
  empCode: string
  empName: string | null
  departCode: string | null
  position: string | null
  approverLevel: number
}

type Day = {
  workDate: string
  dow: number
  isHoliday: boolean
  isFuture: boolean
  shiftStart: string | null
  shiftEnd: string | null
  punchIn: string | null
  punchOut: string | null
  lateMinute: number
  late: boolean
  absent: boolean
  onLeave: boolean
  leaveTypeDesc: string | null
}

type WorkData = {
  employee: { empCode: string }
  round: Round
  rounds: Round[]
  range: { from: string; to: string }
  days: Day[]
  summary: Record<string, number>
}

/** ใบลา/ปรับปรุงเวลา (รายปี) ของลูกน้อง — จาก leave.getOverview */
type LeaveForm = {
  recId: number
  rowId: string | null
  desc: string
  workDate: string | null
  dateStart: string | null
  dateEnd: string | null
  hours: number | null
  reason: string | null
  statusText: string
  statusTone: string
}

type LeaveData = {
  year: number
  years: number[]
  quota: {
    leaveType: string
    desc: string
    dayCanUsed: number | null
    dayUsed: number
    dayRemain: number | null
    entitlementFrom: 'HR_CAN_LEAVE' | 'HR_LEAVE_TYPE' | null
    hasQuotaRow: boolean
  }[]
  leaves: LeaveForm[]
  adjustments: LeaveForm[]
  summary: { leaveCount: number; adjustmentCount: number; leaveDays: number }
}

/** OT ตามรอบของลูกน้อง — จาก ot.listRange */
type OtData = {
  round: Round
  rounds: Round[]
  range: { from: string; to: string }
  items: {
    rowId: string | number
    otDate: string
    otShiftText: string
    timeStart: string | null
    timeEnd: string | null
    hours: number
    rate: number | null
    statusText: string
    statusGroup: string
  }[]
  summary: { count: number; hours: number; byRate: { rate: number; hours: number }[] }
}

type Tab = 'worktime' | 'leave' | 'ot'

function minutesText(min: number): string {
  if (!min || min <= 0) return '—'
  const h = Math.floor(min / 60)
  const m = min % 60
  return h ? `${h} ชม.${m ? ` ${m} น.` : ''}` : `${m} น.`
}

function dayStatus(d: Day): { label: string; tone: string } {
  if (d.isHoliday) return { label: 'วันหยุด', tone: 'chip chip--off' }
  if (d.isFuture) return { label: 'ยังไม่ถึง', tone: 'chip chip--off' }
  if (d.onLeave) return { label: d.leaveTypeDesc ?? 'ลา', tone: 'chip chip--warn' }
  if (d.absent) return { label: 'ขาดงาน', tone: 'chip chip--bad' }
  if (d.late) return { label: 'สาย', tone: 'chip chip--warn' }
  return { label: 'ตรงเวลา', tone: 'chip chip--ok' }
}

/** โทนสถานะจาก backend → คลาสชิป */
const toneClass = (tone: string) =>
  tone === 'ok' ? 'chip--ok' : tone === 'bad' ? 'chip--bad' : 'chip--warn'

export default function MyTeam() {
  const [team, setTeam] = useState<Member[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [q, setQ] = useState('')
  const [selected, setSelected] = useState<Member | null>(null)

  useEffect(() => {
    api<{ team: Member[] }>('/api/me/team')
      .then((d) => {
        setTeam(d.team)
        setError(null)
        // เลือกคนแรกให้เลย — แผงขวาว่างเปล่าตอนเปิดหน้าไม่ได้บอกอะไร
        if (d.team.length > 0) setSelected(d.team[0])
      })
      .catch((e: unknown) => setError(errorText(e)))
  }, [])

  const term = q.trim().toLowerCase()
  const shown = (team ?? []).filter((m) =>
    term === ''
      ? true
      : [m.empCode, m.empName, m.position, m.departCode]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(term)),
  )

  return (
    <>
      <h1>ข้อมูลทีมของฉัน</h1>
      <p className="meta">
        ลูกน้องในสายของคุณ (คนที่ตั้งคุณเป็นผู้อนุมัติ) — กดชื่อเพื่อดูเวลาทำงาน การลา และ OT
      </p>

      {error && <p className="error">{error}</p>}

      <div className="team-page">
        <section className="card">
          <div className="section-head">
            <h2>
              ลูกน้อง{' '}
              <span className="meta">
                {team === null ? '' : term ? `${shown.length}/${team.length} คน` : `${team.length} คน`}
              </span>
            </h2>
          </div>

          {/* ค้นหา — หัวหน้าบางคนมีลูกน้อง 29 คน เลื่อนหาชื่อเองช้ากว่าพิมพ์ */}
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="ค้นชื่อ รหัส หรือตำแหน่ง"
            aria-label="ค้นหาลูกน้อง"
          />

          {team === null ? (
            <p className="meta">กำลังโหลด…</p>
          ) : team.length === 0 ? (
            <p className="meta">ไม่มีลูกน้องในสายของคุณ</p>
          ) : shown.length === 0 ? (
            <p className="meta">ไม่พบชื่อที่ค้น</p>
          ) : (
            <div className="pick-list">
              {shown.map((m) => (
                <button
                  key={m.empCode}
                  type="button"
                  className={
                    selected?.empCode === m.empCode
                      ? 'pick-list__item pick-list__item--on'
                      : 'pick-list__item'
                  }
                  onClick={() => setSelected(m)}
                  aria-current={selected?.empCode === m.empCode}
                >
                  <strong>{m.empName ?? m.empCode}</strong>
                  <div className="meta">
                    {m.empCode}
                    {m.position ? ` · ${m.position}` : ''}
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>

        <div>
          {selected ? (
            <MemberDetail member={selected} />
          ) : (
            <section className="card">
              <p className="meta">เลือกพนักงานจากรายชื่อด้านซ้าย</p>
            </section>
          )}
        </div>
      </div>
    </>
  )
}

/** แผงรายละเอียดลูกน้องคนหนึ่ง — เวลาทำงาน / การลา / OT */
function MemberDetail({ member }: { member: Member }) {
  const [tab, setTab] = useState<Tab>('worktime')

  const TABS: { key: Tab; label: string }[] = [
    { key: 'worktime', label: 'เวลาทำงาน' },
    { key: 'leave', label: 'การลา' },
    { key: 'ot', label: 'OT' },
  ]

  return (
    <section className="card">
      <div className="section-head">
        <div>
          <h2>{member.empName ?? member.empCode}</h2>
          <p className="meta">
            {member.empCode}
            {member.position ? ` · ${member.position}` : ''} · คุณเป็นผู้อนุมัติชั้น{' '}
            {member.approverLevel}
          </p>
        </div>
      </div>

      <div className="tabs" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            className={tab === t.key ? 'tab tab--on' : 'tab'}
            onClick={() => setTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* key={empCode} — เปลี่ยนคนต้องโหลดใหม่ ไม่ใช่โชว์ข้อมูลคนเก่าค้างไว้ */}
      {tab === 'worktime' && <WorktimeTab key={member.empCode} member={member} />}
      {tab === 'leave' && <LeaveTab key={member.empCode} member={member} />}
      {tab === 'ot' && <OtTab key={member.empCode} member={member} />}
    </section>
  )
}

/** แท็บ "เวลาทำงาน" — ตารางเวลาเข้า–ออกรายวันของลูกน้อง */
function WorktimeTab({ member }: { member: Member }) {
  const [round, setRound] = useState('')
  const [data, setData] = useState<WorkData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    setLoading(true)
    const q = round ? `?round=${encodeURIComponent(round)}` : ''
    api<WorkData>(`/api/me/team/${encodeURIComponent(member.empCode)}/worktime${q}`)
      .then((d) => {
        if (!alive) return
        setData(d)
        setError(null)
      })
      .catch((e: unknown) => alive && setError(errorText(e)))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [member.empCode, round])

  const s = data?.summary ?? {}

  return (
    <>
      {data && (
        <RoundPicker
          value={data.round.roundId}
          rounds={data.rounds}
          onChange={setRound}
          disabled={loading}
        />
      )}

      {error && <p className="error">{error}</p>}

      {data && (
        <p className="meta">
          {roundLabel(data.round)} · มาทำงาน {s.completeDays ?? 0} วัน · ขาด {s.absentDays ?? 0} ·
          สาย {s.lateDays ?? 0} · ลา {s.leaveDays ?? 0}
        </p>
      )}

      {data === null ? (
        <p className="meta">กำลังโหลด…</p>
      ) : data.days.length === 0 ? (
        <p className="meta">ไม่มีข้อมูลในรอบนี้</p>
      ) : (
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th className="nowrap">วันที่</th>
                <th>วัน</th>
                <th className="nowrap">กำหนด</th>
                <th className="nowrap">ตอกจริง</th>
                <th className="num">สาย</th>
                <th>สถานะ</th>
              </tr>
            </thead>
            <tbody>
              {data.days.map((d) => {
                const st = dayStatus(d)
                const weekend = d.dow === 0 || d.dow === 6
                return (
                  <tr
                    key={d.workDate}
                    className={
                      d.isHoliday ? 'wt-row--holiday' : weekend ? 'wt-row--weekend' : undefined
                    }
                  >
                    <td className="nowrap">{dayLabel(d.workDate)}</td>
                    <td className="meta">{dowLabel(d.dow)}</td>
                    <td className="nowrap meta">
                      {d.shiftStart && d.shiftEnd ? `${d.shiftStart}–${d.shiftEnd}` : '—'}
                    </td>
                    <td className="nowrap">
                      {d.punchIn ?? '—'}
                      {d.punchIn || d.punchOut ? ' – ' : ''}
                      {d.punchOut ?? (d.punchIn ? '—' : '')}
                    </td>
                    <td className="num">{d.lateMinute ? minutesText(d.lateMinute) : '—'}</td>
                    <td>
                      <span className={st.tone}>{st.label}</span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

/** แท็บ "การลา" — โควตา + ใบลา + ใบปรับปรุงเวลา รายปี */
function LeaveTab({ member }: { member: Member }) {
  const [year, setYear] = useState('')
  const [data, setData] = useState<LeaveData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    setLoading(true)
    const q = year ? `?year=${encodeURIComponent(year)}` : ''
    api<LeaveData>(`/api/me/team/${encodeURIComponent(member.empCode)}/leave${q}`)
      .then((d) => {
        if (!alive) return
        setData(d)
        setError(null)
      })
      .catch((e: unknown) => alive && setError(errorText(e)))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [member.empCode, year])

  return (
    <>
      {data && (
        <label className="kv">
          <span className="kv-label">ปี</span>
          <select
            value={String(data.year)}
            onChange={(e) => setYear(e.target.value)}
            disabled={loading}
          >
            {data.years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>
      )}

      {error && <p className="error">{error}</p>}
      {data === null ? (
        <p className="meta">กำลังโหลด…</p>
      ) : (
        <>
          <h3>โควต้าวันลา</h3>
          {data.quota.length === 0 ? (
            <p className="meta">ยังไม่มีโควต้าของปี {data.year}</p>
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>ประเภท</th>
                    <th className="num">ได้</th>
                    <th className="num">ใช้ไป</th>
                    <th className="num">คงเหลือ</th>
                  </tr>
                </thead>
                <tbody>
                  {data.quota.map((item) => (
                    <tr key={item.leaveType}>
                      <td>
                        {item.desc}
                        {item.dayCanUsed === null ? (
                          <span className="meta"> (ไม่ได้กำหนดเพดาน)</span>
                        ) : item.entitlementFrom === 'HR_LEAVE_TYPE' ? (
                          <span className="meta"> (สิทธิ์ตามประเภท)</span>
                        ) : null}
                      </td>
                      <td className="num nowrap">{dayHourMin(item.dayCanUsed)}</td>
                      <td className="num nowrap">{dayHourMin(item.dayUsed)}</td>
                      <td className="num nowrap">{dayHourMin(item.dayRemain)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <h3>
            ใบลา{' '}
            <span className="meta">
              {data.summary.leaveCount} ใบ · {dayHourMin(data.summary.leaveDays)}
            </span>
          </h3>
          <LeaveFormTable rows={data.leaves} empty={`ไม่มีใบลาในปี ${data.year}`} />

          <h3>
            ใบปรับปรุงเวลา <span className="meta">{data.summary.adjustmentCount} ใบ</span>
          </h3>
          <LeaveFormTable
            rows={data.adjustments}
            empty={`ไม่มีใบปรับปรุงเวลาในปี ${data.year}`}
            isAdjust
          />
        </>
      )}
    </>
  )
}

/** ตารางใบลา/ใบปรับปรุงเวลา — ใบลาคิดเป็นวัน · ใบปรับปรุงเวลาคิดเป็นชั่วโมง */
function LeaveFormTable({
  rows,
  empty,
  isAdjust = false,
}: {
  rows: LeaveForm[]
  empty: string
  isAdjust?: boolean
}) {
  if (rows.length === 0) return <p className="meta">{empty}</p>
  const stamp = (iso: string | null) => (iso ? iso.slice(0, 16).replace('T', ' ') : '—')
  return (
    <div className="table-wrap">
      <table className="data">
        <thead>
          <tr>
            <th>ประเภท</th>
            <th className="nowrap">ตั้งแต่</th>
            <th className="nowrap">ถึง</th>
            <th className="num">เวลา</th>
            <th>เหตุผล</th>
            <th>สถานะ</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((f) => (
            <tr key={f.recId}>
              <td className="nowrap">{f.desc}</td>
              <td className="nowrap">{f.dateStart ? stamp(f.dateStart) : (f.workDate ?? '—')}</td>
              <td className="nowrap">{stamp(f.dateEnd)}</td>
              <td className="num nowrap">
                {f.hours === null ? '—' : isAdjust ? hoursToHM(f.hours) : dayHourMin(f.hours / 8)}
              </td>
              <td className="cell-clip" title={f.reason ?? ''}>
                {f.reason ?? '—'}
              </td>
              <td className="nowrap">
                <span className={`chip ${toneClass(f.statusTone)}`}>{f.statusText}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** แท็บ "OT" — ชั่วโมงล่วงเวลาตามรอบ */
function OtTab({ member }: { member: Member }) {
  const [round, setRound] = useState('')
  const [data, setData] = useState<OtData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let alive = true
    setLoading(true)
    const q = round ? `?round=${encodeURIComponent(round)}` : ''
    api<OtData>(`/api/me/team/${encodeURIComponent(member.empCode)}/ot${q}`)
      .then((d) => {
        if (!alive) return
        setData(d)
        setError(null)
      })
      .catch((e: unknown) => alive && setError(errorText(e)))
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
  }, [member.empCode, round])

  return (
    <>
      {data && (
        <RoundPicker
          value={data.round.roundId}
          rounds={data.rounds}
          onChange={setRound}
          disabled={loading}
        />
      )}

      {error && <p className="error">{error}</p>}
      {data === null ? (
        <p className="meta">กำลังโหลด…</p>
      ) : (
        <>
          <p className="meta">
            {roundLabel(data.round)} · รวม <strong>{hoursToHM(data.summary.hours)}</strong> ·{' '}
            {data.summary.count} รายการ
            {data.summary.byRate.length > 0 && (
              <>
                {' · '}
                {data.summary.byRate.map((r, i) => (
                  <span key={r.rate}>
                    {i > 0 && ' · '}x{r.rate} {hoursToHM(r.hours)}
                  </span>
                ))}
              </>
            )}
          </p>

          {data.items.length === 0 ? (
            <p className="meta">ไม่มี OT ในรอบนี้</p>
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th className="nowrap">วันที่</th>
                    <th>ช่วง</th>
                    <th className="nowrap">เวลา</th>
                    <th className="num">ชั่วโมง</th>
                    <th className="num">เรต</th>
                    <th>สถานะ</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((it) => (
                    <tr key={`${it.rowId}-${it.otDate}`}>
                      <td className="nowrap">{dayLabel(it.otDate)}</td>
                      <td>{it.otShiftText}</td>
                      <td className="nowrap">
                        {it.timeStart ?? '—'} – {it.timeEnd ?? '—'}
                      </td>
                      <td className="num nowrap">{hoursToHM(it.hours)}</td>
                      <td className="num">{it.rate === null ? '—' : `x${it.rate}`}</td>
                      <td className="nowrap">
                        <span
                          className={`chip ${
                            it.statusGroup === 'approved'
                              ? 'chip--ok'
                              : it.statusGroup === 'rejected'
                                ? 'chip--bad'
                                : 'chip--warn'
                          }`}
                        >
                          {it.statusText}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </>
  )
}
